import React from 'react'
import { motion, type Variants } from 'framer-motion'
import Plus from '~/components/icons/Plus'
import { cn } from '~/utils/cn'
import Typography, { type TypographyProps } from '../Typography'

export type AccordionProps = PropsWithClassName<{
  children: React.ReactNode
  title: React.ReactNode
  TitleProps?: Partial<TypographyProps<'p'>>
  separator?: 'bottom' | 'top'
  isDefaultOpened?: boolean
  testId: string
}>

const variants = {
  enter: {
    y: 0,
    opacity: 1,
    height: 'auto',
    transition: {
      height: {
        ease: 'easeInOut',
        duration: 0.3
      },
      opacity: {
        easings: 'ease',
        duration: 0.5
      }
    }
  },
  exit: {
    y: -10,
    opacity: 0,
    height: 0,
    transition: {
      height: {
        easings: 'ease',
        duration: 0.25
      },
      opacity: {
        easings: 'ease',
        duration: 0.3
      }
    }
  }
} satisfies Variants

const Accordion = ({
  title,
  TitleProps,
  children,
  className,
  separator = 'bottom',
  isDefaultOpened = false,
  testId
}: AccordionProps) => {
  const [isOpen, toggleIsOpen] = React.useReducer((state) => {
    return !state
  }, isDefaultOpened)

  return (
    <div
      itemScope
      className={cn('flex flex-col border-black/10', className, {
        'bg-black bg-opacity-[0.02]': isOpen,
        'border-t': separator === 'top',
        'border-b': separator === 'bottom'
      })}
      itemType="https://schema.org/Question"
    >
      <button
        type="button"
        className="flex cursor-pointer items-center justify-between gap-x-6 py-6 text-left text-body1 text-black"
        onClick={toggleIsOpen}
        itemProp="name"
        data-testid={testId}
      >
        <Typography
          className="flex-1"
          variant="body2"
          component="div"
          {...TitleProps}
        >
          {title}
        </Typography>
        <div className="shrink-0">
          <motion.div
            className={cn(
              'flex aspect-square w-[44px] shrink-0 cursor-pointer items-center justify-center rounded-full border p-2 transition-colors',
              {
                'bg-black text-white': isOpen
              }
            )}
            initial={{ rotate: 0 }}
            animate={{ rotate: isOpen ? 45 : 0 }}
          >
            <Plus />
          </motion.div>
        </div>
      </button>
      <motion.div
        variants={variants}
        initial={isDefaultOpened ? 'enter' : 'exit'}
        animate={isOpen ? 'enter' : 'exit'}
        className="overflow-hidden"
      >
        <div className="pb-6">{children}</div>
      </motion.div>
    </div>
  )
}

export default Accordion
